import { getTextDelta } from '@databases/react/useTextDelta';
import { DefaultRootState } from '@databases/redux';
import {
  Database,
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
  ImportedFileType,
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  DatabaseChange,
  MultiValueChangeType,
  MultiValueFieldType,
  ValueChange,
} from '@databases/utils/database/changesets';
import { exportToHtml } from '@databases/utils/database/export';
import {
  applyPageLabels,
  getContentLabels,
  getReferencedLabelContentRefs,
  PageLabelsDelta,
} from '@databases/utils/database/field-types/pageLabels';
import {
  applyPageStatus,
  DEFAULT_PAGE_STATUS_COLOR,
  getContentStatus,
  getReferencedStatusContentRefs,
} from '@databases/utils/database/field-types/pageStatus';
import {
  createEntry,
  createField,
  getConvertedDatabaseValue,
  getExistingFieldNames,
  getUniqueFieldName,
} from '@databases/utils/database/utils';
import { getLabelsDelta } from '@databases/utils/deltas/labels-delta';
import { parseStorageFormat } from '@databases/utils/lib/dom';
import { SimpleStore } from '@databases/utils/redux/redux';
import { normalizeText } from '@databases/utils/utils';
import { isEmpty } from 'lodash-es';

import {
  ImportEntryMode,
  ImportEntryReference,
  ImportFieldMode,
  ImportFieldReference,
  ImportResult,
  ImportSettings,
} from '../types';

import { supportedImportMimeTypes } from './importHelper';

/**
 * The supported paste MIME types.
 */
export const supportedPasteMimeTypes: Record<string, ImportedFileType> = {
  'text/html': ImportedFileType.HTML,
  ...supportedImportMimeTypes,
};

/**
 * Determines the missing rows or columns based on the given all rows, selected rows and imported rows.
 * @param allItems - All rows or columns
 * @param selectedItems - Selected rows or columns
 * @param importedItems - Imported rows or columns
 * @returns the last 'n' entries of importedRows, where 'n' is the difference between the sum of the index and the length of importedRows,
 * and the length of allRows. Otherwise, returns an empty array.
 */
export const getMissingItems = <A>(
  allItems: string[],
  selectedItems: string[],
  importedItems: Array<A>
) => {
  const selectedIndex = allItems.indexOf(selectedItems[0]);

  const itemsToReturn = selectedIndex + importedItems.length - allItems.length;

  if (itemsToReturn > 0) {
    // Return the last items of the importedItems
    return importedItems.slice(-itemsToReturn);
  } else {
    return [];
  }
};

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.EXISTING taking the
 * fieldIds and entryIds as input and starting from the selected indexes until the end of the fieldIds and entryIds.
 * @returns the import settings for the selected field and entry ids.
 */
export const getImportSettingsFromSelection = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[]
): ImportSettings => ({
  fieldReferences: fieldIds
    .slice(fieldIds.indexOf(selectedFieldIds[0]))
    .map((fieldId) => ({
      type: ImportFieldMode.EXISTING,
      fieldId,
    })),
  entryReferences: entryIds
    .slice(entryIds.indexOf(selectedEntryIds[0]))
    .map((entryId) => ({
      type: ImportEntryMode.EXISTING,
      entryId,
    })),
  selectedRows: selectedEntryIds.map(() => true),
  selectedColumns: selectedFieldIds.map(() => true),
});

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.CREATE and
 * ImportFieldMode.CREATE. Takes the settings for the selection and appends the missing fields and entries to the fieldReferences and
 * entryReferences. New fields are created with a unique name.
 * @returns the import settings for the selected field and entry ids as well as the missing fields and entries.
 */
export const getImportSettingsWithMissingItems = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[],
  database: Database,
  importResult: ImportResult,
  fieldLabel: string
): ImportSettings => {
  const settings = getImportSettingsFromSelection(
    selectedFieldIds,
    selectedEntryIds,
    fieldIds,
    entryIds
  );
  const missingEntries = getMissingItems(entryIds, selectedEntryIds, importResult.rows);
  const missingFields = getMissingItems(
    fieldIds,
    selectedFieldIds,
    importResult.rows[0].cols
  );
  const addedFieldNames: string[] = [];
  return {
    ...settings,
    fieldReferences: [
      ...settings.fieldReferences,
      ...missingFields.map(() => {
        const uniqueFieldName = getUniqueFieldName(fieldLabel, [
          ...getExistingFieldNames(database),
          ...addedFieldNames,
        ]);
        addedFieldNames.push(uniqueFieldName);
        return {
          type: ImportEntryMode.CREATE,
          field: {
            ...createField(DatabaseFieldType.TEXT),
            name: uniqueFieldName,
          },
        };
      }),
    ] as ImportFieldReference[],
    entryReferences: [
      ...settings.entryReferences,
      ...missingEntries.map(() => ({
        type: ImportFieldMode.CREATE,
        entry: createEntry(database),
      })),
    ] as ImportEntryReference[],
  };
};

// Soft selection helpers
export const getLastFieldIdByChangeType = (
  changeset: DatabaseChange[],
  changesetType: DatabaseChangeType
) => {
  return changeset
    .filter((change) => change.type === changesetType)
    .map((change) => {
      if ('field' in change) {
        return change.field.id;
      }

      if ('fieldId' in change) {
        return change.fieldId;
      }
    })
    .pop();
};

export const getLastEntryIdByChangeType = (
  changeset: DatabaseChange[],
  changesetType: DatabaseChangeType
) => {
  return changeset
    .filter((change) => change.type === changesetType)
    .map((change) => {
      if ('entry' in change) {
        return change.entry.id;
      }

      if ('entryId' in change) {
        return change.entryId;
      }
    })
    .pop();
};

/**
 * Returns the value change (delta) for the pasted value for the given field
 * or null if the value should not be applied or the field is read only.
 * For some fields, the value change is applied to the referenced content.
 **/
export function applyDeltaAfterPaste(
  {
    entry,
    field,
    value,
    database,
    text,
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    value: DatabaseValue | undefined;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
): ValueChange[] | null {
  const previousDatabaseValue = getConvertedDatabaseValue(
    database,
    field,
    entry,
    getState()
  );
  switch (field.type) {
    // read only fields
    case DatabaseFieldType.JIRA_ISSUE_DETAILS:
    case DatabaseFieldType.ENTRY_DETAILS:
    case DatabaseFieldType.BACKLINK:
    case DatabaseFieldType.PAGE_DETAILS: {
      return null;
    }

    // fields which has to be applied to referenced content
    case DatabaseFieldType.PAGE_LABELS: {
      replacePageLabels({ entry, field, database, text }, { getState, dispatch });
      return null;
    }
    case DatabaseFieldType.PAGE_STATUS: {
      replacePageStatus({ entry, field, value, database, text }, { getState, dispatch });
      return null;
    }

    // single value fields
    case DatabaseFieldType.TEXT:
      return [
        {
          type: field.type,
          delta: getTextDelta(
            (previousDatabaseValue as DatabaseValue<DatabaseFieldType.TEXT>)?.value ?? '',
            (value as DatabaseValue<DatabaseFieldType.TEXT>)?.value ?? ''
          ),
        },
      ];
    case DatabaseFieldType.NUMBER: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.NUMBER>)?.value,
        },
      ];
    }
    case DatabaseFieldType.CHECKBOX: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.CHECKBOX>)?.value,
        },
      ];
    }
    case DatabaseFieldType.DATE: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.DATE>)?.value,
        },
      ];
    }

    // multi value fields
    case DatabaseFieldType.HYPERLINK: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
    case DatabaseFieldType.ENTRY_LINK: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
    case DatabaseFieldType.USER: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
    case DatabaseFieldType.JIRA_ISSUE: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
    case DatabaseFieldType.SELECT: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
    case DatabaseFieldType.PAGE_LINK: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
    case DatabaseFieldType.ATTACHMENT: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value,
      });
    }
  }
}

/**
 * Replace the value in a multi-select field
 * */
const replaceMultiFieldValue = ({
  field,
  databaseValue,
}: {
  field: DatabaseField;
  databaseValue?: DatabaseValue;
}): ValueChange[] => {
  const isMultiValue = isMultiValueFieldType(databaseValue, field.type);
  if (!isMultiValue) {
    return [];
  }
  const values = Array.isArray(databaseValue.value)
    ? databaseValue.value
    : [databaseValue.value];

  return [
    {
      type: field.type,
      delta: {
        type: MultiValueChangeType.SET_TO,
        value: values,
      },
    },
  ] as ValueChange[];
};

const replacePageLabels = (
  {
    entry,
    field,
    database,
    text,
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
) => {
  getReferencedLabelContentRefs({ database, field, entry }, getState())?.map(
    (contentRef) => {
      const existingLabels = getContentLabels(contentRef, {}, getState()) ?? [];
      const normalizedLabelsArray = normalizeText(text)?.split(' ') || [];
      const newLabels = isEmpty(text) ? [] : normalizedLabelsArray;
      const labelsToRemove = existingLabels.filter((label) => !newLabels.includes(label));
      const deltas: PageLabelsDelta = { add: [], remove: [] };

      newLabels.forEach((label) => {
        const delta = getLabelsDelta({
          action: 'select-option',
          option: { value: label, label },
        });
        if (delta) {
          deltas.add.push(...delta.add);
        }
      });

      labelsToRemove.forEach((label) => {
        const delta = getLabelsDelta({
          action: 'deselect-option',
          option: { value: label, label },
        });
        if (delta) {
          deltas.remove.push(...delta.remove);
        }
      });

      if (field.settings.allowEdit) {
        applyPageLabels(contentRef, deltas, { getState, dispatch });
      }
    }
  );
};

const replacePageStatus = (
  {
    entry,
    field,
    database,
    value,
    text,
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    value: DatabaseValue | undefined;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
) => {
  const newValue = (value as DatabaseValue<DatabaseFieldType.PAGE_STATUS>)?.value;
  getReferencedStatusContentRefs({ database, field, entry }, getState())?.map(
    (contentRef) => {
      const existingStatus =
        newValue && getContentStatus(contentRef, newValue, getState());
      if (text && field.settings.allowEdit && existingStatus?.name !== text) {
        const newStatus = {
          name: text,
          color: DEFAULT_PAGE_STATUS_COLOR,
        };
        applyPageStatus(contentRef, newStatus, { getState, dispatch });
      }
    }
  );
};

const isMultiValueFieldType = (
  value?: DatabaseValue,
  type?: DatabaseFieldType
): value is DatabaseValue<MultiValueFieldType | DatabaseFieldType.HYPERLINK> => {
  return value?.type === type;
};

const isStorageFormat = (value: string) => {
  const parsedStorageFormat = parseStorageFormat(value);
  const allElements = parsedStorageFormat.find('*');
  // All prefixes found on https://confluence.atlassian.com/doc/confluence-storage-format-790796544.html
  const prefixes = ['ac:', 'ri:', 'at:'];
  return Array.from(allElements).some((element) =>
    prefixes.some((prefix) => element.name.startsWith(prefix))
  );
};

export const convertToHtmlFormat = async (
  databaseName: string,
  state: DefaultRootState,
  items: {
    type: string;
    rawContent: string;
  }[]
) => {
  return await Promise.all(
    items.map(async (item) => {
      if (item.type === 'text/html' && isStorageFormat(item.rawContent)) {
        const htmlFormat = await exportToHtml(item.rawContent, databaseName, state, true);
        return {
          type: item.type,
          rawContent: htmlFormat,
        };
      } else {
        return item;
      }
    })
  );
};
